<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import {useRouter} from 'vue-router';
import {AvailabilityState, CabMe} from 'common';
import {logout} from '@/base/auth.js';
import {clearMe, getMeInfo} from '@/base/me.js';
import {stateForReporters} from '@/base/TMP_globalState.js';
import {ElDropdown, ElDropdownItem, ElDropdownMenu, ElIcon} from 'element-plus';
import {ArrowDown} from '@element-plus/icons-vue';

const router = useRouter();

const meInfo         = ref<CabMe | undefined>(undefined);
const userName       = computed(() => getUserName());
const userInitials   = computed(() => getUserInitials());
const stateSuffix    = computed(() => getStateSuffix());
const userPermission = computed(() => {
    const perms = meInfo.value?.permissions?.join() ?? '';
    return stateSuffix.value ? `${perms} (${stateSuffix.value})` : perms;
});

onMounted(async () => {
    meInfo.value = await getMeInfo();
});

async function onLogout() {
    try {
        await logout();
    } finally {
        clearMe();
        router.push('/');
    }
}

function getUserName(): string {
    return meInfo.value?.name || meInfo.value?.email || '?';
}

function getUserInitials(): string {
    const woorden: string[] = getUserName().split(/\s+/).filter((w: string) => w !== '');
    return woorden.map((w: string) => w[0]).join('').toUpperCase() || '?';
}

function getStateSuffix() {
    if (meInfo.value?.permissions?.includes('report') ?? false) {
        switch (stateForReporters.value) {
            case AvailabilityState.closed:
                return 'geen-toegang';
            case AvailabilityState.read:
                return 'alleen-lezen';
        }
    }
    return '';
}
</script>

<template>
    <el-dropdown trigger="click">
        <button data-testid="current-user-button" class="flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100 cursor-pointer">
            <span class="w-8 h-8 rounded-full bg-zzc-blue text-white text-sm flex items-center justify-center font-semibold shrink-0">
                {{ userInitials }}
            </span>
            <span class="hidden lg:flex flex-col items-start leading-tight">
                <span class="text-sm text-gray-700 whitespace-nowrap">{{ userName }}</span>
                <span v-if="stateSuffix" class="text-xs text-orange-600">{{ stateSuffix }}</span>
            </span>
            <el-icon class="text-gray-400"><ArrowDown/></el-icon>
        </button>
        <template #dropdown>
            <div class="px-4 py-2 border-b border-gray-100 min-w-48">
                <div class="text-sm text-gray-800">{{ userName }}</div>
                <div data-testid="current-user-permission" class="text-xs text-gray-500">{{ userPermission }}</div>
            </div>
            <el-dropdown-menu>
                <el-dropdown-item @click="onLogout">Uitloggen</el-dropdown-item>
            </el-dropdown-menu>
        </template>
    </el-dropdown>
</template>
